"use client";

import { useId, useState } from "react";
import classnames from "classnames";

import styles from "./accordion.module.scss";

// ─── Controlled accordion (external isOpen/onToggle) ───

export interface AccordionItemProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export function AccordionItem({ title, isOpen, onToggle, children }: AccordionItemProps) {
  const sectionId = useId();

  return (
    <div className={styles.accordion}>
      <button
        className={styles.header}
        onClick={onToggle}
        type="button"
        aria-expanded={isOpen}
        aria-controls={sectionId}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isOpen ? styles.chevronOpen : styles.chevronClosed}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
        {title}
      </button>
      <div
        id={sectionId}
        className={classnames(styles.collapsibleWrapper, !isOpen && styles.collapsed)}
      >
        <div className={styles.collapsibleInner}>
          <div className={styles.body}>{children}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Uncontrolled section (internal state, optional collapsibility) ───

export interface SectionProps {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export function Section({
  title,
  children,
  collapsible = false,
  defaultExpanded = true,
}: SectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentId = useId();

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={classnames(styles.sectionHeader, !collapsible && styles.static)}
        onClick={() => collapsible && setExpanded(!expanded)}
        disabled={!collapsible}
        aria-expanded={collapsible ? expanded : undefined}
        aria-controls={collapsible ? contentId : undefined}
      >
        <span className={styles.sectionTitle}>{title}</span>
        {collapsible && (
          <span className={classnames(styles.sectionChevron, expanded && styles.sectionExpanded)}>
            &#8250;
          </span>
        )}
      </button>
      <div
        id={contentId}
        className={classnames(styles.collapsibleWrapper, !expanded && styles.collapsed)}
      >
        <div className={styles.collapsibleInner}>
          <div className={styles.sectionContent}>{children}</div>
        </div>
      </div>
    </div>
  );
}
